/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { Swatch } from '@/components/blocks/Swatch';
import {
	facetNavigationFacetEntryFormControlLabelSX,
	facetNavigationFacetEntrySwatchSX,
} from '@/components/content/FacetNavigation/styles/facetEntry/formControlLabel';
import { useFacetNavigation } from '@/data/Content/FacetNavigation';
import { ContentContext } from '@/data/context/content';
import { ProductFacetEntry } from '@/data/types/Product';
import { Box, Checkbox, FormControlLabel, SxProps, Typography } from '@mui/material';
import { FC, useCallback, useContext } from 'react';
import CheckIcon from '@mui/icons-material/Check';

type Props = {
	entry: ProductFacetEntry;
	isRangePrice?: boolean;
};

const CheckboxCustom = ({ isActive, sx }: { isActive: boolean; sx?: SxProps }) => (
	<Box
		sx={{
			height: '100%',
			width: '100%',
			backgroundColor: isActive ? '#0064C7' : 'transparent',
			border: `0.5px solid ${isActive ? '#0064C7' : '#C9C9C9'}`,
			borderRadius: '4px',
			...sx,
		}}
	>
		{isActive ? <CheckIcon sx={{ height: '100%', width: '100%', color: 'white' }} /> : null}
	</Box>
);

const MINS = {
	min: 0,
	max: 1,
};

export const FacetNavigationEntry: FC<Props> = ({ entry, isRangePrice }) => {
	const { selectedFacet, onFacetSelectChange, onPriceRangeChange } = useContext(
		ContentContext
	) as ReturnType<typeof useFacetNavigation>;
	const onChange = useCallback(
		(event: any) => {
			onFacetSelectChange(event, entry);
		},
		[entry, onFacetSelectChange]
	);

	const minMax = (value: ProductFacetEntry) =>
		value.label.replaceAll(' ', '').replace('({', '').replace('])', '').split('TO');
	const submit = (min: number, max: number) => {
		onPriceRangeChange({
			minPrice: min ?? MINS.min,
			maxPrice: !max || Number.isNaN(max) ? 99999 : max,
		});
	};

	const formatLabelRangePrice = (value: ProductFacetEntry) => {
		let newLabel = '';
		const partsOfLabel = minMax(value);

		if (partsOfLabel[0] === '*') {
			newLabel = `Menos de $${partsOfLabel[1]}`;
		} else if (partsOfLabel[1] === '*') {
			newLabel = `Mas de $${partsOfLabel[0]}`;
		} else {
			newLabel = `$${partsOfLabel[0]} - $${partsOfLabel[1]}`;
		}

		newLabel = `${newLabel} `;

		return newLabel;
	};
	return entry.image ? (
		<Swatch
			aria-label={entry.label}
			image={entry.image}
			onClick={onChange}
			size="large"
			selected={selectedFacet !== undefined ? selectedFacet.indexOf(entry.value) > -1 : undefined}
			data-testid={`product-filter-${entry.value.toLowerCase()}-swatch`}
			id={`product-filter-${entry.value.toLowerCase()}-swatch`}
			sx={facetNavigationFacetEntrySwatchSX}
		/>
	) : (
		<FormControlLabel
			sx={facetNavigationFacetEntryFormControlLabelSX}
			control={
				<Checkbox
					icon={<CheckboxCustom isActive={false} />}
					checkedIcon={<CheckboxCustom isActive={true} />}
					sx={{
						p: 0,
						ml: '4px',
						mr: 1,
						height: '17px',
						width: '17px',
					}}
					checked={
						selectedFacet !== undefined ? selectedFacet.indexOf(entry.value) > -1 : undefined
					}
					onChange={
						isRangePrice
							? () => {
									const tempMinMax = minMax(entry);
									submit(Number(tempMinMax[0]), Number(tempMinMax[1]));
							  }
							: onChange
					}
				/>
			}
			label={
				isRangePrice ? (
					<Typography
						component={'span'}
						sx={{
							font: 'normal normal normal 14px/20px Open Sans',
							letterSpacing: '0px',
							color: '#2C2C2C',
						}}
					>
						{`${formatLabelRangePrice(entry)}`}
						<Typography component={'span'} color={'#929292'}>{`(${entry.count})`}</Typography>
					</Typography>
				) : (
					<Typography
						component={'span'}
						sx={{
							font: 'normal normal normal 14px/20px Open Sans',
							letterSpacing: '0px',
							color: '#2C2C2C',
						}}
					>
						{entry.label}{' '}
						<Typography component={'span'} color={'#929292'}>{`(${entry.count})`}</Typography>{' '}
					</Typography>
				)
			}
			id={isRangePrice ? `${formatLabelRangePrice(entry)}` : `${entry.label} (${entry.count})`}
			data-testid={
				isRangePrice ? `${formatLabelRangePrice(entry)}` : `${entry.label} (${entry.count})`
			}
		/>
	);
};
